import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { FaRegEyeSlash, FaEye } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { UpdatePasswordSchema } from "../../components/Helpers";
import Hoc from "../../components/HOC";
import {
  requestUpdatePassword,
  setResetUpdatePassword,
} from "../../Redux/LoginState/LoginActionCreator";
import { toast } from "react-toastify";
import ModalComponent from "../../components/Modal";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../helpers/Localstorage";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Progress } from "../../components/Progress";
import { CheckCircle2Icon, XCircleIcon } from "lucide-react";

function UpdatePassword() {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [missingCriteria, setMissingCriteria] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [customErrorMessage, setCustomErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const UpdatePasswordState = useSelector(
    (state) => state.UpdatePasswordReducer
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);

  // const checkPasswordStrength = (password) => {
  //   const criteria = [
  //     { test: (pw) => /[a-z]/.test(pw), message: "Include a lowercase letter" },
  //     {
  //       test: (pw) => /[A-Z]/.test(pw),
  //       message: "Include an uppercase letter",
  //     },
  //     { test: (pw) => /\d/.test(pw), message: "Include a number" },
  //     {
  //       test: (pw) => /[^a-zA-Z\d\s:?,]/.test(pw),
  //       message: "Include a special character",
  //     }, // Exclude "?"
  //     { test: (pw) => pw.length >= 8, message: "At least 8 characters long" },
  //   ];

  //   const unmetCriteria = criteria
  //     .filter((criterion) => !criterion.test(password))
  //     .map((criterion) => criterion.message);

  //   setMissingCriteria(unmetCriteria);
  //   setPasswordStrength(criteria.length - unmetCriteria.length);
  // };

  const checkPasswordStrength = (password) => {
    const criteria = [
      { test: (pw) => /[a-z]/.test(pw), message: "Lowercase letter" },
      { test: (pw) => /[A-Z]/.test(pw), message: "Uppercase letter" },
      { test: (pw) => /\d/.test(pw), message: "Number" },
      {
        test: (pw) => /[^a-zA-Z\d\s:?,]/.test(pw),
        message: "Special character",
      },
      { test: (pw) => pw.length >= 8, message: "8+ characters" },
    ];

    const unmetCriteria = criteria
      .filter((criterion) => !criterion.test(password))
      .map((criterion) => criterion.message);

    setMissingCriteria(unmetCriteria);
    setPasswordStrength(criteria.length - unmetCriteria.length);
  };

  const renderStrengthBar = () => {
    if (missingCriteria.length === 0) return null;

    const colors = [
      "bg-red-500",
      "bg-yellow-500",
      "bg-primary",
      "bg-green-500",
    ];
    const width = `${(passwordStrength / 5) * 100}%`;

    return (
      <div className="relative h-2 w-full bg-gray-200 rounded mt-2">
        <div
          className={`absolute top-0 left-0 h-2 rounded transition-all ${
            colors[passwordStrength - 1] || "bg-red-500"
          }`}
          style={{ width }}
        ></div>
      </div>
    );
  };
  const getStrengthColor = () => {
    if (passwordStrength <= 2) return "bg-red-500";
    if (passwordStrength <= 3) return "bg-amber-500";
    return "bg-green-500";
  };
  const getStrengthText = () => {
    if (passwordStrength <= 2) return "Weak";
    if (passwordStrength <= 3) return "Medium";
    return "Strong";
  };

  const resetPassword = (values) => {
    dispatch(requestUpdatePassword(values));
  };
  useEffect(() => {
    if (
      UpdatePasswordState?.updateResponse &&
      !UpdatePasswordState?.updateLoading
    ) {
      setIsSubmitted(true);
      toast.success("Password updated successfully");

      dispatch(setResetUpdatePassword());
    }

    if (
      UpdatePasswordState?.updateError &&
      !UpdatePasswordState?.updateLoading
    ) {
      const errorMessage = UpdatePasswordState?.updateError?.message;
      if (errorMessage) {
        Swal.fire({
          title: errorMessage,
          icon: "warning",
          showCancelButton: false,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Ok",
          backdrop: `rgba(0, 0, 0, 0.5)`,
        }).then((result) => {
          if (result.isConfirmed) {
            dispatch(setResetUpdatePassword());
          }
        });
      } else {
        toast.error("Unable to update password");
        dispatch(setResetUpdatePassword());
      }
    }
  }, [UpdatePasswordState]);

  useEffect(() => {
    return () => {
      dispatch(setResetUpdatePassword());
    };
  }, []);

  return (
    <div className="w-full max-h-[90vh]   overflow-y-scroll p-5">
      <Formik
        initialValues={{
          newPassword: "",
          confirmPassword: "",
          oldPassword: "",
        }}
        validationSchema={UpdatePasswordSchema}
        onSubmit={(values, { resetForm }) => {
          if (values.newPassword === values.confirmPassword) {
            resetPassword(values);
            resetForm();
          } else {
            toast.error("Passwords do not match");
            dispatch(setResetUpdatePassword());
          }
        }}
      >
        {({ errors, touched, resetForm, values }) => (
          <div className="flex flex-row justify-center items-center pt-16">
            <div>
              <Form className="bg-white p-8 rounded-lg shadow-none w-[30vw] ">
                <h1 className="text-xl text-gray-800 font-semibold mb-6 text-center">
                  Update Password
                </h1>

                <div className="mb-4 relative">
                  <label className="text-black-700 mb-2 text-lg">
                    Old Password
                  </label>
                  <Field
                    name="oldPassword"
                    type={passwordVisible ? "text" : "password"}
                    className="w-full p-1 border mb-6 rounded focus:outline-none hover:ring-2 hover:ring-blue-400 "
                  />
                  {passwordVisible ? (
                    <FaEye
                      onClick={() => setPasswordVisible(false)}
                      className="absolute right-3 top-[34px] cursor-pointer"
                    />
                  ) : (
                    <FaRegEyeSlash
                      onClick={() => setPasswordVisible(true)}
                      className="absolute right-3 top-[34px] cursor-pointer"
                    />
                  )}
                  {errors.oldPassword && touched.oldPassword && (
                    <p className="text-red-400">{errors.newPassword}</p>
                  )}
                </div>

                {/* New Password Field */}
                <div className="mb-4 relative">
                  <label className="text-black mb-2 text-lg">
                    New Password
                  </label>
                  <Field
                    name="newPassword"
                    type={isPasswordVisible ? "text" : "password"}
                    className="w-full p-1 border mb-6 rounded focus:outline-none hover:ring-2 hover:ring-blue-400"
                    onKeyUp={(e) => checkPasswordStrength(e.target.value)}
                  />
                  {isPasswordVisible ? (
                    <FaEye
                      onClick={() => setIsPasswordVisible(false)}
                      className="absolute right-3 top-[34px] cursor-pointer"
                    />
                  ) : (
                    <FaRegEyeSlash
                      onClick={() => setIsPasswordVisible(true)}
                      className="absolute right-3 top-[34px] cursor-pointer"
                    />
                  )}
                  {errors.newPassword && touched.newPassword && (
                    <p className="text-red-400">{errors.newPassword}</p>
                  )}
                </div>
                {values.newPassword && (
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">
                        Password strength:
                      </span>
                      <span
                        className={`text-sm font-medium ${
                          passwordStrength <= 2
                            ? "text-red-500"
                            : passwordStrength <= 3
                            ? "text-amber-500"
                            : "text-green-500"
                        }`}
                      >
                        {getStrengthText()}
                      </span>
                    </div>
                    <Progress
                      value={(passwordStrength / 5) * 100}
                      className={`h-2 ${getStrengthColor()}`}
                    />
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[
                        "Lowercase letter",
                        "Uppercase letter",
                        "Number",
                        "Special character",
                        "8+ characters",
                      ].map((criterion) => (
                        <div
                          key={criterion}
                          className="flex items-center space-x-2"
                        >
                          {missingCriteria?.includes(criterion) ? (
                            <XCircleIcon className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle2Icon className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-xs">{criterion}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Confirm Password Field */}
                <div className="mb-4 relative">
                  <label className="text-black-700 mb-2 text-lg">
                    Confirm Password
                  </label>
                  <Field
                    name="confirmPassword"
                    type={isConfirmPasswordVisible ? "text" : "password"}
                    className="w-full p-1 border mb-6 rounded text-sm focus:outline-none hover:ring-2 hover:ring-blue-400"
                  />
                  {isConfirmPasswordVisible ? (
                    <FaEye
                      onClick={() => setIsConfirmPasswordVisible(false)}
                      className="absolute right-3 top-[34px] cursor-pointer"
                    />
                  ) : (
                    <FaRegEyeSlash
                      onClick={() => setIsConfirmPasswordVisible(true)}
                      className="absolute right-3 top-[34px] cursor-pointer"
                    />
                  )}
                  {errors.confirmPassword && touched.confirmPassword && (
                    <p className="text-red-400">{errors.confirmPassword}</p>
                  )}
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    className="w-full text-white font-medium text-sm bg-primary hover:bg-primaryDark px-4 py-2 rounded"
                  >
                    {"Submit"}
                  </button>

                  {customErrorMessage && (
                    <p className="mt-2 text-sm text-red-500 text-center">
                      {customErrorMessage}
                    </p>
                  )}
                </div>
              </Form>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
}
export default Hoc(UpdatePassword);
